import {
  BufferAttribute,
  Color,
  Intersection,
  LineBasicMaterial,
  LineSegments,
  Vector2,
  Vector3,
} from 'three';

import { isDefined } from '../../../utils/variables';
import {
  Conic,
  getConicDirection,
  getConicParameter,
} from '../../elements/design/design-node/utils/conic';
import { isStyledLines, StyledLinesIntersection } from '../../elements/styled-lines/styled-lines';
import { LineReference } from './line-constraints';
import { TrajectorySnapTarget } from './snap-to-trajectory';

/**
 * A line of an asset, e.g. of a CAD drawing or another design, in world coordinates.
 */
export type AssetLine = { start: Vector3; end: Vector3 } | { conic: Conic };

/**
 * Gets the line segment of an intersection with lines: the start and end in the coordinates of the
 * object. For indexed lines the intersection only has the index of the start vertex, so the line
 * segment that starts there and is closest to the intersection is used.
 */
export function getIntersectedSegment(object: LineSegments, intersection: Intersection) {
  const position = object.geometry.getAttribute('position') as BufferAttribute | undefined;
  const index = object.geometry.getIndex();
  const start = intersection.index;

  if (!position || !isDefined(start)) {
    return undefined;
  }

  const vertex = (i: number) => new Vector3().fromBufferAttribute(position, i);

  if (!index) {
    return start + 1 < position.count ? [vertex(start), vertex(start + 1)] : undefined;
  }

  const point = object.worldToLocal(intersection.point.clone());
  let closest: [Vector3, Vector3] | undefined;
  let closestDistance = Infinity;

  for (let i = 0; i + 1 < index.count; i += 2) {
    if (index.getX(i) !== start) {
      continue;
    }

    const a = vertex(start);
    const b = vertex(index.getX(i + 1));
    const line = b.clone().sub(a);
    const t = Math.min(1, Math.max(0, point.clone().sub(a).dot(line) / (line.lengthSq() || 1)));
    const distance = a.clone().addScaledVector(line, t).distanceTo(point);

    if (distance < closestDistance) {
      closestDistance = distance;
      closest = [a, b];
    }
  }

  return closest;
}

/**
 * Gets the line or curve that an intersection with an asset is on, in world coordinates, or
 * undefined if it is not on a line, e.g. on a surface or a point.
 */
export function getAssetLine(intersection?: Intersection): AssetLine | undefined {
  const object = intersection?.object;

  if (isStyledLines(object)) {
    const { item } = intersection as StyledLinesIntersection;
    const toWorld = (p: Vector3) => p.clone().applyMatrix4(object.matrixWorld);

    if (item.kind === 'curve') {
      return {
        conic: {
          start: toWorld(item.start),
          control: toWorld(item.control),
          end: toWorld(item.end),
          weight: item.weight,
        },
      };
    }

    return item.kind === 'straight' && !item.start.equals(item.end)
      ? { start: toWorld(item.start), end: toWorld(item.end) }
      : undefined;
  }

  if (object instanceof LineSegments && intersection) {
    const segment = getIntersectedSegment(object, intersection);

    return segment && !segment[0].equals(segment[1])
      ? { start: object.localToWorld(segment[0]), end: object.localToWorld(segment[1]) }
      : undefined;
  }

  return undefined;
}

/**
 * Gets the color that the intersected line is drawn with, of an asset or of the design, or undefined
 * if it is not a line.
 */
export function getIntersectedLineColor(intersection?: Intersection): Color | undefined {
  const object = intersection?.object;

  if (isStyledLines(object)) {
    return (intersection as StyledLinesIntersection).item.color.clone();
  }

  if (!(object instanceof LineSegments) || !intersection) {
    return undefined;
  }

  const material = (Array.isArray(object.material) ? object.material[0] : object.material) as
    | LineBasicMaterial
    | undefined;
  const color = material?.color?.clone() ?? new Color(1, 1, 1);
  const vertexColors = object.geometry.getAttribute('color') as BufferAttribute | undefined;

  // The vertex colors are multiplied with the color of the material.
  if (material?.vertexColors && vertexColors && isDefined(intersection.index)) {
    color.multiply(new Color().fromBufferAttribute(vertexColors, intersection.index));
  }

  return color;
}

/**
 * Gets the target to snap a trajectory to on the asset line, at the hovered position.
 */
export function getAssetLineTarget(line: AssetLine, position: Vector3): TrajectorySnapTarget {
  return 'conic' in line
    ? { type: 'curve', position, conic: line.conic }
    : { type: 'line', position, start: line.start, end: line.end };
}

/**
 * Gets the asset line as the reference of angles from a point on it: its direction, or for a curve
 * its direction at the point.
 */
export function getAssetLineReference(line: AssetLine, point: Vector3): LineReference {
  if ('conic' in line) {
    const direction = getConicDirection(line.conic, getConicParameter(line.conic, point));
    const tangent = new Vector3(direction.x, direction.y, 0);

    return {
      direction,
      line: { start: point.clone().sub(tangent), end: point.clone().add(tangent) },
    };
  }

  const direction = line.end.clone().sub(line.start);

  return {
    direction: new Vector2(direction.x, direction.y).normalize(),
    line: { start: line.start, end: line.end },
  };
}
